import { createContext, useContext, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import TourOverlay from "./TourOverlay";
import { TOURS } from "../../lib/tours";

const TourCtx = createContext({ activeTour: null, startTour: () => {}, endTour: () => {} });

/**
 * Interactive guided-tour provider. A tour is a list of steps, each pointing
 * at a DOM element (CSS selector) with a title + body. The overlay draws a
 * spotlight around the element and a tooltip with Back / Next / Skip.
 * Tours are registered in /lib/tours and started by id: startTour("add-yacht").
 */
export function TourProvider({ children }) {
  const nav = useNavigate();
  const [tour, setTour] = useState(null); // { id, steps }

  const startTour = useCallback(
    (tourId) => {
      const def = TOURS[tourId];
      if (!def) return;
      if (def.route) nav(def.route);
      // Small delay so navigation settles before the overlay starts looking
      // for the first step's target element.
      setTimeout(() => setTour({ id: tourId, steps: def.steps }), 150);
    },
    [nav],
  );

  const endTour = useCallback(() => {
    setTour(null);
    // Tour steps open real dialogs (Add item / New task / …) via each
    // step's onEnter. Closing the overlay alone would leave that dialog
    // sitting open ("active window"). The dialogs suppress Escape *while a
    // tour is active*, so we clear the tour first, then fire Escape on the
    // next tick once activeTour has cleared — Radix's DismissableLayer
    // picks it up and closes whatever dialog the tour left open.
    setTimeout(() => {
      document.dispatchEvent(
        new KeyboardEvent("keydown", { key: "Escape", code: "Escape", bubbles: true }),
      );
    }, 80);
  }, []);

  return (
    <TourCtx.Provider value={{ activeTour: tour?.id || null, startTour, endTour }}>
      {children}
      {tour && <TourOverlay steps={tour.steps} onClose={endTour} />}
    </TourCtx.Provider>
  );
}

export const useTour = () => useContext(TourCtx);
